import { useMemo } from 'react';

import { useLoggedInUser } from '@wac-web/utils/logged-in-user-provider';

import { useRoutes } from '@/common/hooks';
import { useFastshiftSelector } from '@/state';

import { SourceKey } from '../constants';

import { useBaseIdentityAttrs } from './use-base-identity-attrs';
import { useCustomerIdentityAttrs } from './use-customer-identity-attrs';

import type { ErrorIdentityAttrs, IdentityAttrs } from '../types';

type AnalyticsIdentityAttrs = IdentityAttrs | ErrorIdentityAttrs | null;

/**
 * Provides analytics data with auto-detection for ineligible pages
 * @param allowFallback - Set to true for UI events, false for critical events (page/track)
 */
export const useFastshiftAnalyticsData = (allowFallback = false) => {
	const baseIdentityAttrs = useBaseIdentityAttrs();
	const customerIdentityAttrs = useCustomerIdentityAttrs();
	const { domainId, fullPathname, paths } = useRoutes();

	const { error: userError } = useFastshiftSelector((state) => state.userAccess);

	const loggedInUser = useLoggedInUser();
	const userDomain = loggedInUser.email ? (loggedInUser.email.split('@')[1] ?? '') : '';

	const sourceKey = useMemo(() => {
		if (userError) {
			return SourceKey.FASTSHIFT_PORTAL_GENERIC_ERROR_SCREEN;
		}
		const pathToScreenMap: Record<string, SourceKey> = {
			[paths.selection]: SourceKey.FASTSHIFT_PORTAL_SELECTION_SCREEN,
			[paths.partnerResources]: SourceKey.FASTSHIFT_PORTAL_PARTNER_RESOURCES_SCREEN,
		};

		// Add domain-scoped paths if they exist
		if (paths.domainScopedOverview) {
			pathToScreenMap[paths.domainScopedOverview] = SourceKey.FASTSHIFT_PORTAL_FOR_YOU_SCREEN;
		}
		if (paths.domainScopedPlanning) {
			pathToScreenMap[paths.domainScopedPlanning] =
				SourceKey.FASTSHIFT_PORTAL_MIGRATION_PLANNING_SCREEN;
		}
		if (paths.domainScopedResources) {
			pathToScreenMap[paths.domainScopedResources] =
				SourceKey.FASTSHIFT_PORTAL_CUSTOMER_RESOURCES_SCREEN;
		}
		if (paths.domainScopedTickets) {
			pathToScreenMap[paths.domainScopedTickets] =
				SourceKey.FASTSHIFT_PORTAL_MIGRATION_TICKETS_SCREEN;
		}
		if (paths.domainScopedOrganizations) {
			pathToScreenMap[paths.domainScopedOrganizations] =
				SourceKey.FASTSHIFT_PORTAL_ORGANIZATIONS_SCREEN;
		}
		if (paths.domainScopedOrganizationDetails) {
			pathToScreenMap[paths.domainScopedOrganizationDetails] =
				SourceKey.FASTSHIFT_PORTAL_ORGANIZATION_DETAILS_SCREEN;
		}

		return pathToScreenMap[fullPathname] || SourceKey.FASTSHIFT_PORTAL_404_SCREEN;
	}, [userError, fullPathname, paths]);

	const isDomainScoped = !!domainId;

	const { strictIdentityAttrs, uiIdentityAttrs } = useMemo<{
		strictIdentityAttrs: AnalyticsIdentityAttrs;
		uiIdentityAttrs: AnalyticsIdentityAttrs;
	}>(() => {
		const strictAttrs = isDomainScoped ? customerIdentityAttrs : baseIdentityAttrs;
		const uiAttrs = strictAttrs ?? baseIdentityAttrs;

		if (userError && userDomain) {
			return {
				strictIdentityAttrs: { ...strictAttrs, userDomain },
				uiIdentityAttrs: { ...uiAttrs, userDomain },
			};
		}

		return {
			strictIdentityAttrs: strictAttrs,
			uiIdentityAttrs: uiAttrs,
		};
	}, [isDomainScoped, customerIdentityAttrs, baseIdentityAttrs, userError, userDomain]);

	return {
		sourceKey,
		identityAttrs: allowFallback ? uiIdentityAttrs : strictIdentityAttrs,
		uiIdentityAttrs,
	};
};
